// BackgroundRotator.tsx
import React, { useEffect, useRef, useState } from 'react';

type BackgroundRotatorProps = {
    images: string[];
    intervalMs?: number;
    transitionMs?: number;
};

const DEFAULT_INTERVAL = 5000;
const DEFAULT_TRANSITION = 600;

const BackgroundRotator: React.FC<BackgroundRotatorProps> = ({
    images,
    intervalMs = DEFAULT_INTERVAL,
    transitionMs = DEFAULT_TRANSITION,
}) => {
    const [baseIndex, setBaseIndex] = useState(0);
    const [overlayIndex, setOverlayIndex] = useState<number | null>(null);
    const [overlayVisible, setOverlayVisible] = useState(false);

    const baseIndexRef = useRef(0);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const rafRef = useRef<number | null>(null);
    const overlayIndexRef = useRef<number | null>(null);

    const imagesCount = images.length;
    if (!imagesCount) return null;

    // ensure indices stay within bounds if images array changes
    useEffect(() => {
        setBaseIndex((idx) => (imagesCount ? idx % imagesCount : 0));
        baseIndexRef.current = imagesCount ? baseIndexRef.current % imagesCount : 0;
    }, [imagesCount]);

    // Preload to reduce flicker
    useEffect(() => {
        images.forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, [images]);

    useEffect(() => {
        baseIndexRef.current = baseIndex;
    }, [baseIndex]);

    useEffect(() => {
        if (imagesCount <= 1) return;

        const tick = () => {
            if (overlayIndexRef.current !== null) return; // still animating
            const next = (baseIndexRef.current + 1) % imagesCount;
            if (next === baseIndexRef.current) return;
            overlayIndexRef.current = next;
            setOverlayIndex(next);
        };

        intervalRef.current = setInterval(tick, intervalMs);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
        };
    }, [imagesCount, intervalMs]);

    useEffect(() => {
        if (overlayIndex === null) return;

        // start overlay hidden, then fade in next frame
        setOverlayVisible(false);

        if (rafRef.current !== null && typeof window !== 'undefined') {
            window.cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            rafRef.current = window.requestAnimationFrame(() => setOverlayVisible(true));
        } else {
            setOverlayVisible(true);
        }

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            if (overlayIndex === null) return;
            setBaseIndex(overlayIndex);
            baseIndexRef.current = overlayIndex;
            setOverlayIndex(null);
            overlayIndexRef.current = null;
            setOverlayVisible(false);
        }, transitionMs);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
            if (rafRef.current !== null && typeof window !== 'undefined') {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [overlayIndex, transitionMs]);

    useEffect(
        () => () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (rafRef.current !== null && typeof window !== 'undefined') {
                window.cancelAnimationFrame(rafRef.current);
            }
        },
        []
    );

    const baseImage = images[baseIndex];
    const overlayImage = overlayIndex !== null ? images[overlayIndex] : null;

    return (
        <div className="loading-bg" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }} aria-hidden>
            <div
                className="loading-bg-layer"
                style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url(${baseImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            />

            {overlayImage && (
                <div
                    className="loading-bg-layer"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url(${overlayImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: overlayVisible ? 1 : 0,
                        transition: `opacity ${transitionMs}ms ease`,
                        willChange: 'opacity',
                    }}
                />
            )}
        </div>
    );
};

export default BackgroundRotator;
